import { useTranslation } from 'react-i18next';

import { errorMessageKey } from '@/shared/lib/errors';

import { Button } from './Button';
import { Spinner } from './Spinner';

interface AsyncProps<T> {
  query: {
    data: T | undefined;
    isPending: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
  };
  isEmpty?: (data: T) => boolean;
  emptyMessage?: string;
  /** Custom pending view (e.g. skeleton cards). Defaults to a centered spinner. */
  loading?: JSX.Element;
  children: (data: T) => JSX.Element;
}

/**
 * Standard loading / error / empty / data contract for React Query results
 * (instructions/04_UI_UX.md). Every data view uses this.
 */
export function Async<T>({
  query,
  isEmpty,
  emptyMessage,
  loading,
  children,
}: AsyncProps<T>): JSX.Element {
  const { t } = useTranslation();

  if (query.isPending) {
    return (
      loading ?? (
        <div className="state">
          <Spinner />
          <span className="muted">{t('common.loading')}</span>
        </div>
      )
    );
  }

  if (query.isError) {
    return (
      <div className="state" role="alert">
        <p>{t(errorMessageKey(query.error))}</p>
        <Button variant="outline" onClick={() => query.refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const data = query.data as T;
  if (isEmpty?.(data)) {
    return (
      <div className="state">
        <p className="muted">{emptyMessage ?? t('common.empty')}</p>
      </div>
    );
  }

  return children(data);
}
